// web/src/lib/promptSuggestions.ts
// Builds the blended, ranked suggestion list shown under the single chat box.
//
// Two sources, one list:
//   • Recent  — the user's own past Luna questions (re-runnable starters)
//   • Catalog — luna_capabilities.json, profile-ranked when idle, query-ranked
//               while typing
//
// No separate search box, no command palette — this powers the in-composer
// type-ahead panel (see PromptSuggestions.tsx). Selecting an item sends the
// full prompt immediately.
import capabilitiesData from "../data/luna_capabilities.json";
import { suggestCapabilities } from "./suggestCapabilities";
const ALL = capabilitiesData;
// Featured fall-back for the idle list when profile ranking is thin (new
// users). Spans styles so the empty state teaches breadth, not just taxes.
const FEATURED_IDS = [
    "trade-conviction",
    "retirement-projection",
    "tax-yearend",
    "portfolio-stress-2008",
    "goals-emergency",
    "portfolio-allocation",
];
const MAX_RECENT = 3;
const MAX_CATALOG_EMPTY = 6;
const MAX_CATALOG_QUERY = 8;
function truncate(s, n = 96) {
    return s.length > n ? s.slice(0, n - 1) + "…" : s;
}
function recentToSuggestion(text, i) {
    const t = text.trim();
    return { key: `recent-${i}`, text: truncate(t), prompt: t, kind: "recent" };
}
function capToSuggestion(c) {
    return { key: `cap-${c.id}`, text: truncate(c.prompt), prompt: c.prompt, group: c.group, kind: "catalog" };
}
/**
 * The curated, profile-ranked capabilities shown as cards on the empty-chat
 * landing (and as the idle "Try asking" list). Profile ranking first, padded
 * with featured picks so new users still see a diverse, inviting set.
 */
export function landingCapabilities(profile, n) {
    const ranked = suggestCapabilities(ALL, profile ?? null, n);
    const seen = new Set(ranked.map((c) => c.id));
    for (const id of FEATURED_IDS) {
        if (ranked.length >= n)
            break;
        if (seen.has(id))
            continue;
        const c = ALL.find((x) => x.id === id);
        if (c) {
            ranked.push(c);
            seen.add(id);
        }
    }
    return ranked.slice(0, n);
}
function matchScore(c, q) {
    const label = c.label.toLowerCase();
    const group = c.group.toLowerCase();
    const prompt = c.prompt.toLowerCase();
    const desc = c.description.toLowerCase();
    if (label.startsWith(q))
        return 100;
    if (label.includes(q))
        return 80;
    if (group.includes(q))
        return 60;
    if (prompt.includes(q))
        return 40;
    if (desc.includes(q))
        return 20;
    return 0;
}
// A catalog item whose prompt duplicates a recent is dropped — recent wins,
// since it's the user's own phrasing.
function dedupe(sections) {
    const recentTexts = new Set(sections
        .filter((s) => s.items[0]?.kind === "recent")
        .flatMap((s) => s.items.map((i) => i.prompt.toLowerCase().trim())));
    return sections
        .map((s) => ({
        ...s,
        items: s.items.filter((i) => i.kind === "recent" || !recentTexts.has(i.prompt.toLowerCase().trim())),
    }))
        .filter((s) => s.items.length > 0);
}
/**
 * Build the suggestion sections for the current input.
 *   - empty query → Recent + profile-ranked "Try asking"
 *   - non-empty   → matched recents + catalog matches grouped by area
 */
export function buildSuggestions(query, profile, recents) {
    const q = query.trim().toLowerCase();
    if (q.length === 0) {
        const recentItems = recents.slice(0, MAX_RECENT).map(recentToSuggestion);
        const ranked = landingCapabilities(profile, MAX_CATALOG_EMPTY);
        const sections = [];
        if (recentItems.length)
            sections.push({ title: "Recent", items: recentItems });
        if (ranked.length) {
            sections.push({ title: "Try asking", items: ranked.map(capToSuggestion) });
        }
        return dedupe(sections);
    }
    const recentMatches = recents
        .filter((r) => r.toLowerCase().includes(q))
        .slice(0, MAX_RECENT)
        .map(recentToSuggestion);
    const scored = ALL.map((c) => ({ c, s: matchScore(c, q) }))
        .filter((x) => x.s > 0)
        .sort((a, b) => b.s - a.s)
        .slice(0, MAX_CATALOG_QUERY)
        .map((x) => x.c);
    const sections = [];
    if (recentMatches.length)
        sections.push({ title: "Recent", items: recentMatches });
    // Group catalog matches by area, in rank order of first appearance.
    const order = [];
    const byGroup = new Map();
    for (const c of scored) {
        if (!byGroup.has(c.group)) {
            byGroup.set(c.group, []);
            order.push(c.group);
        }
        byGroup.get(c.group).push(c);
    }
    for (const g of order) {
        sections.push({ title: g, items: byGroup.get(g).map(capToSuggestion) });
    }
    return dedupe(sections);
}
